import React from 'react';
import { ArrowLeftRight, X } from 'lucide-react';

interface SelectionActionBarProps {
  selectedCount: number;
  maxSelection: number;
  onMerge: () => void;
  onCancel: () => void;
  mergeLabel?: string;
}

/** Floating dark pill above the mobile tab bar for acting on a row selection. */
export const SelectionActionBar: React.FC<SelectionActionBarProps> = ({
  selectedCount,
  maxSelection,
  onMerge,
  onCancel,
  mergeLabel = 'Merge',
}) => {
  const canMerge = selectedCount === maxSelection;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4 pb-4 font-ui sm:pb-6"
      style={{ bottom: 'var(--app-tabbar-h, 0px)' }}
    >
      <div
        role="toolbar"
        aria-label="Selection"
        className="pointer-events-auto flex h-12 w-full max-w-md animate-fade-up items-center justify-between gap-2 rounded-full bg-ink pl-5 pr-1.5 text-white shadow-[0_20px_48px_-16px_rgba(0,0,0,0.45)] ring-1 ring-white/10 sm:w-auto sm:min-w-[380px]"
      >
        <p className="min-w-0 truncate text-[13px] tabular-nums text-neutral-300" aria-live="polite">
          <span className="text-white">{selectedCount}</span> of {maxSelection} selected
        </p>

        <div className="flex flex-shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel"
            className="inline-flex h-9 items-center gap-1.5 rounded-full px-2.5 text-[13px] text-neutral-400 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 sm:px-3"
          >
            <X className="h-4 w-4" strokeWidth={1.75} />
            <span className="hidden sm:inline">Cancel</span>
          </button>
          <button
            type="button"
            onClick={onMerge}
            disabled={!canMerge}
            className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary-600 px-4 text-[13px] font-medium text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/40"
          >
            <ArrowLeftRight className="h-4 w-4" strokeWidth={1.75} />
            {mergeLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
